import React, { useEffect, useRef, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Loader2, ImagePlus, X, Save, Sparkles, ScrollText, Link2, Plus, Trash2 } from 'lucide-react';
import { contentAPI, uploadAPI, mediaUrl } from '../services/api';

const SECRET_NAMES = {
  1: 'Équilibre calories / dépenses',
  2: 'Les protéines, moteur du métabolisme',
  3: 'Le petit-déjeuner, clé de l\'énergie',
  4: 'Les bons glucides et l\'index glycémique',
  5: 'Les bonnes graisses',
};

const ImageEditor = ({ images, setImages, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image')) { toast.error('Choisis une image'); return; }
    setBusy(true);
    try {
      const res = await uploadAPI.upload(file);
      setImages([...(images || []), res.url]);
    } catch { toast.error("Échec de l'image"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {(images || []).map((img, i) => (
          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden">
            <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => ref.current?.click()} data-testid={testid} className="text-sm font-semibold text-red-600 flex items-center gap-1.5">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}Ajouter une photo
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={add} />
    </div>
  );
};

export const CoachSecrets = () => {
  const [n, setN] = useState(1);
  const [data, setData] = useState({ title: '', text: '', images: [], links: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    contentAPI.secret(n).then((d) => setData({ title: d.title || '', text: d.text || '', images: d.images || [], links: d.links || [] })).catch(() => {});
  }, [n]);

  const updateLink = (i, field, val) => setData((d) => ({ ...d, links: d.links.map((l, j) => (j === i ? { ...l, [field]: val } : l)) }));
  const addLink = () => setData((d) => ({ ...d, links: [...(d.links || []), { label: '', url: '' }] }));
  const removeLink = (i) => setData((d) => ({ ...d, links: d.links.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await contentAPI.setSecret({ secret_number: n, ...data });
      toast.success('Secret enregistré');
    } catch { toast.error('Échec'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
      <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-red-600" />Présentation des secrets</h3>
      <p className="text-xs text-muted-foreground mb-3">Texte + photos + liens affichés dans « Secret du moment » pour les challengers.</p>
      <div className="flex gap-2 mb-4">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} onClick={() => setN(s)} data-testid={`secret-tab-${s}`}
            className={`flex-1 py-2 rounded-lg text-sm font-bold ${n === s ? 'bg-red-600 text-white' : 'bg-muted text-muted-foreground'}`}>{s}</button>
        ))}
      </div>
      <p className="text-xs font-semibold text-muted-foreground mb-3">Secret {n} — {SECRET_NAMES[n]}</p>
      <div className="space-y-3">
        <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="secret-title" placeholder="Titre (optionnel)" className="rounded-lg" />
        <Textarea value={data.text} onChange={(e) => setData({ ...data, text: e.target.value })} data-testid="secret-text" rows={4} placeholder="Explication du secret... (tu peux coller des liens https://... ils seront cliquables)" className="rounded-lg resize-none" />
        <ImageEditor images={data.images} setImages={(imgs) => setData({ ...data, images: imgs })} testid="secret-add-image" />
        <div className="space-y-2 pt-1">
          <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" />Boutons de liens vers des pages internet</p>
          {(data.links || []).map((lnk, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={lnk.label || ''} onChange={(e) => updateLink(i, 'label', e.target.value)} placeholder="Libellé" data-testid={`secret-link-label-${i}`} className="rounded-lg flex-1 h-9 text-sm" />
              <Input value={lnk.url || ''} onChange={(e) => updateLink(i, 'url', e.target.value)} placeholder="https://..." data-testid={`secret-link-url-${i}`} className="rounded-lg flex-1 h-9 text-sm" />
              <button onClick={() => removeLink(i)} data-testid={`secret-link-remove-${i}`} className="w-9 h-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addLink} data-testid="secret-add-link" className="text-sm font-semibold text-red-600 flex items-center gap-1.5"><Plus className="w-4 h-4" />Ajouter un lien</button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-secret" className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl font-bold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer le secret {n}
        </Button>
      </div>
    </Card>
  );
};

export const CoachReglement = () => {
  const [data, setData] = useState({ text: '', images: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    contentAPI.reglement().then((d) => setData({ text: d.text || '', images: d.images || [] })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await contentAPI.setReglement(data);
      toast.success('Règlement enregistré');
    } catch { toast.error('Échec'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
      <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><ScrollText className="w-5 h-5 text-red-600" />Règlement du challenge</h3>
      <p className="text-xs text-muted-foreground mb-3">Consultable par tous les challengers.</p>
      <div className="space-y-3">
        <Textarea value={data.text} onChange={(e) => setData({ ...data, text: e.target.value })} data-testid="reglement-text" rows={8} placeholder="Écris le règlement du challenge..." className="rounded-lg resize-none" />
        <ImageEditor images={data.images} setImages={(imgs) => setData({ ...data, images: imgs })} testid="reglement-add-image" />
        <Button onClick={save} disabled={saving} data-testid="save-reglement" className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl font-bold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer le règlement
        </Button>
      </div>
    </Card>
  );
};
